import React, { useState, useEffect, useCallback } from 'react';
import './RankingPanel.css';
import { DEFAULT_WEIGHTS, SNOW_WEIGHTS } from '../config/weight-presets';
import ranking1Icon from '../assets/icons/ranking-1.png';
import ranking2Icon from '../assets/icons/ranking-2.png';
import ranking3Icon from '../assets/icons/ranking-3.png';
import skiData from '../services/ski-data';

const PAGE_SIZE = 100;

const RankingPanel = ({ onMarkersUpdate, selectedResort, onResortSelect }) => {
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [displayResorts, setDisplayResorts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showWeightSection, setShowWeightSection] = useState(false);
  const [showFilterSection, setShowFilterSection] = useState(false);
  const [selectedContinents, setSelectedContinents] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [continents, setContinents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const generateResortDetails = useCallback((resort) => {
    const details = [];
    
    // 基础指标
    if (weights.rating > 0 && resort.rating) {
      details.push(`评分${Number(resort.rating).toFixed(1)}`);
    }
    if (weights.totalTrails > 0 && resort.totalTrails) {
      const trails = resort.totalTrails;
      const formattedTrails = trails >= 10 ? Math.round(trails) : trails.toFixed(1);
      details.push(`总长${formattedTrails}km`);
    }
    if (weights.verticalDrop > 0 && resort.verticalDrop) {
      details.push(`落差${Math.round(resort.verticalDrop)}m`);
    }
    if (weights.lifts > 0 && resort.lifts) {
      details.push(`缆车${Math.round(resort.lifts)}台`);
    }

    // 雪道长度指标
    if (weights.beginnerTrails > 0 && resort.beginnerTrails) {
      const trails = resort.beginnerTrails;
      const formattedTrails = trails >= 10 ? Math.round(trails) : trails.toFixed(1);
      details.push(`初级道${formattedTrails}km`);
    }
    if (weights.intermediateTrails > 0 && resort.intermediateTrails) {
      const trails = resort.intermediateTrails;
      const formattedTrails = trails >= 10 ? Math.round(trails) : trails.toFixed(1);
      details.push(`中级道${formattedTrails}km`);
    }
    if (weights.advancedTrails > 0 && resort.advancedTrails) {
      const trails = resort.advancedTrails;
      const formattedTrails = trails >= 10 ? Math.round(trails) : trails.toFixed(1);
      details.push(`高级道${formattedTrails}km`);
    }

    // 气候指标
    if (weights.snowFall > 0 && resort.snowFall) {
      details.push(`年降雪${Math.round(resort.snowFall)}cm`);
    }
    if (weights.snowDepth > 0 && resort.snowDepth) {
      details.push(`雪均深${Math.round(resort.snowDepth)}cm`);
    }
    if (weights.snowExistDays > 0 && resort.snowExistDays) {
      details.push(`积雪期${Math.round(resort.snowExistDays)}天`);
    }
    if (weights.snowDaysYearly > 0 && resort.snowDaysYearly) {
      details.push(`降雪${Math.round(resort.snowDaysYearly)}日`);
    }

    // 环境指标
    if (weights.temperature > 0 && resort.temperature) {
      details.push(`温度评分${Math.round(resort.temperature)}分`);
    }
    if (weights.windSpeed > 0 && resort.windScore) {
      details.push(`风况评分${Math.round(resort.windScore)}分`);
    }

    // 交通指标
    if (weights.cityDistance > 0 && resort.nearestCityDistance) {
      details.push(`距城市${resort.nearestCityDistance.toFixed(1)}km`);
    }
    if (weights.majorCityDistance > 0 && resort.nearestPrefectureDistance) {
      details.push(`距大城市${resort.nearestPrefectureDistance.toFixed(1)}km`);
    }
    if (weights.airportDistance > 0 && resort.nearestAirportDistance) {
      details.push(`距机场${resort.nearestAirportDistance.toFixed(1)}km`);
    }
    if (weights.trainDistance > 0 && resort.nearestStationDistance) {
      details.push(`距火车站${resort.nearestStationDistance.toFixed(1)}km`);
    }

    // 价格指标
    if (weights.price > 0 && resort.price) {
      details.push(`票价${Math.round(resort.price)}`);
    }
    
    // 将所有详情用 | 连接，不允许换行
    return details.join(' | ').replace(/\n/g, '');
  }, [weights]);

  const formatResortData = useCallback((resorts) => {
    return resorts.map(resort => ({
      ...resort,
      score: resort.score != null ? Number(resort.score).toFixed(1) : '-',
      details: generateResortDetails(resort)
    }));
  }, [generateResortDetails]);

  // 转换滑雪场数据为地图标记
  const convertToMapMarkers = useCallback((resorts) => {
    return resorts.map((resort, index) => ({
      id: resort.id,
      longitude: resort.longitude,
      latitude: resort.latitude,
      isSelected: selectedResort?.id === resort.id,
      rank: index + 1,
      popup: `
        <div class="resort-popup">
          <h3>${resort.name}</h3>
          <p>${resort.country}${resort.region ? ` · ${resort.region}` : ''}</p>
          <p>得分：${resort.score}</p>
          <p>${resort.details}</p>
        </div>
      `
    }));
  }, [selectedResort]);

  // 加载洲际数据
  const loadContinents = useCallback(async () => {
    try {
      const continentsList = await skiData.getContinents();
      const continentsArray = Array.isArray(continentsList) ? continentsList : [];
      setContinents(continentsArray);
    } catch (error) {
      console.error('Failed to load continents:', error);
      setContinents([]);
    }
  }, []);

  // 加载排行榜数据
  const loadRankingData = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        continents: selectedContinents,
        countries: selectedCountries,
        regions: selectedRegions
      };

      const response = await skiData.getFilteredAndRankedResorts(
        filters,
        weights,
        (currentPage - 1) * PAGE_SIZE,
        PAGE_SIZE
      );

      const formattedResorts = formatResortData(response.data || []);
      
      if (currentPage === 1) {
        setDisplayResorts(formattedResorts);
      } else {
        setDisplayResorts(prev => [...prev, ...formattedResorts]);
      }
      
      setHasMore(response.total > currentPage * PAGE_SIZE);
      setError(null);
    } catch (error) {
      console.error('Failed to load ranking data:', error);
      setError(error.message);
      if (currentPage === 1) {
        setDisplayResorts([]);
      }
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    selectedContinents,
    selectedCountries,
    selectedRegions,
    weights,
    formatResortData
  ]);

  // 初始化加载
  useEffect(() => {
    const init = async () => {
      await loadContinents();
      await loadRankingData();
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 处理筛选条件和权重变化
  useEffect(() => {
    setCurrentPage(1);
  }, [weights, selectedContinents, selectedCountries, selectedRegions]);

  // 处理数据加载
  useEffect(() => {
    loadRankingData();
  }, [currentPage, loadRankingData]);

  // 处理标记更新
  useEffect(() => {
    if (displayResorts.length > 0) {
      const markers = convertToMapMarkers(displayResorts);
      onMarkersUpdate(markers);
    }
  }, [displayResorts, selectedResort]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWeightChange = useCallback((type, value) => {
    setWeights(prev => ({
      ...prev,
      [type]: value
    }));
    setCurrentPage(1);
    onResortSelect(null);
  }, [onResortSelect]);

  const handleContinentSelect = useCallback(async (continent) => {
    const newSelected = selectedContinents.includes(continent) 
      ? selectedContinents.filter(c => c !== continent)
      : [...selectedContinents, continent];
    
    setSelectedContinents(newSelected);
    
    // 清除国家和地区的选择
    setSelectedCountries([]);
    setSelectedRegions([]);
    
    if (newSelected.length > 0) {
      const countriesList = await skiData.getCountries(newSelected);
      setCountries(countriesList);
    } else {
      setCountries([]);
    }
    onResortSelect(null);
  }, [selectedContinents, onResortSelect]);

  const handleCountrySelect = useCallback(async (country) => {
    const newSelected = selectedCountries.includes(country)
      ? selectedCountries.filter(c => c !== country)
      : [...selectedCountries, country];
    
    setSelectedCountries(newSelected);
    
    // 清除地区的选择
    setSelectedRegions([]);
    
    if (newSelected.length > 0) {
      const regionsList = await skiData.getRegions(newSelected);
      setRegions(regionsList);
    } else {
      setRegions([]);
    }
    onResortSelect(null);
  }, [selectedCountries, onResortSelect]);

  const handleRegionSelect = useCallback((region) => {
    const newSelected = selectedRegions.includes(region)
      ? selectedRegions.filter(r => r !== region)
      : [...selectedRegions, region];
    
    setSelectedRegions(newSelected);
    onResortSelect(null);
  }, [selectedRegions, onResortSelect]);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
    setCurrentPage(prev => prev + 1);
    }
  }, [loading, hasMore]);

  // 处理滑雪场点击
  const handleResortClick = async (resort) => {
    // 确保我们有 resortId
    if (!resort?.id) {
      console.error('No resort ID provided');
      return;
    }

    try {
      // 直接加载详细信息
      const detail = await skiData.getResortDetail(resort.id);
      onResortSelect(detail);
    } catch (error) {
      console.error('Failed to fetch resort detail:', error);
      // 如果获取详情失败，至少显示基本信息
      onResortSelect(resort);
    }
  };

  return (
    <div className="ranking-container">
    <div className="ranking-panel">
      {/* 控制栏 */}
      <div className="control-bar">
        <div 
          className={`control-item ${showFilterSection ? 'active' : ''}`}
          onClick={() => setShowFilterSection(!showFilterSection)}
        >
          筛选地区
          <span className="toggle-icon">{showFilterSection ? '▼' : '▲'}</span>
        </div>
        <div 
          className={`control-item ${showWeightSection ? 'active' : ''}`}
          onClick={() => setShowWeightSection(!showWeightSection)}
        >
          调整权重
          <span className="toggle-icon">{showWeightSection ? '▼' : '▲'}</span>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* 筛选区域 */}
      {showFilterSection && (
        <div className="filter-section">
          {/* 大洲筛选 */}
          <div className="filter-item">
            <span className="filter-label">大洲</span>
            <div className="filter-tags">
              <span 
                className={`filter-tag ${selectedContinents.length === 0 ? 'is-active' : ''}`}
                onClick={() => setSelectedContinents([])}
              >
                全部
              </span>
              {continents.map(continent => (
                <span 
                  key={continent}
                  className={`filter-tag ${selectedContinents.includes(continent) ? 'is-active' : ''}`}
                  onClick={() => handleContinentSelect(continent)}
                >
                  {continent}
                </span>
              ))}
            </div>
          </div>

          {/* 国家筛选 - 仅在选择了大洲后显示 */}
          {selectedContinents.length > 0 && (
            <div className="filter-item">
              <span className="filter-label">国家</span>
              <div className="filter-tags">
                <span 
                  className={`filter-tag ${selectedCountries.length === 0 ? 'is-active' : ''}`}
                  onClick={() => setSelectedCountries([])}
                >
                  全部
                </span>
                {countries.map(country => (
                  <span 
                    key={country}
                    className={`filter-tag ${selectedCountries.includes(country) ? 'is-active' : ''}`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    {country}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 地区筛选 - 仅在选择了国家后显示 */}
          {selectedCountries.length > 0 && (
            <div className="filter-item">
              <span className="filter-label">地区</span>
              <div className="filter-tags">
                <span 
                  className={`filter-tag ${selectedRegions.length === 0 ? 'is-active' : ''}`}
                  onClick={() => setSelectedRegions([])}
                >
                  全部
                </span>
                {regions.map(region => (
                  <span 
                    key={region}
                    className={`filter-tag ${selectedRegions.includes(region) ? 'is-active' : ''}`}
                    onClick={() => handleRegionSelect(region)}
                  >
                    {region}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 权重调整区域 */}
      {showWeightSection && (
        <div className="weight-section">
            {/* 基础指标 */}
          <div className="weight-group">
            <h3>基础指标</h3>
              <div className="weight-row">
            <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">用户评分</span>
                    <span className="weight-value">{weights.rating}分</span>
                  </div>
              <input 
                type="range" 
                    className="weight-slider"
                min="0" 
                max="50" 
                step="5"
                value={weights.rating}
                onChange={(e) => handleWeightChange('rating', Number(e.target.value))}
              />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">总长度</span>
                    <span className="weight-value">{weights.totalTrails}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.totalTrails}
                    onChange={(e) => handleWeightChange('totalTrails', Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">海拔差</span>
                    <span className="weight-value">{weights.verticalDrop}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.verticalDrop}
                    onChange={(e) => handleWeightChange('verticalDrop', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">缆车数量</span>
                    <span className="weight-value">{weights.lifts}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.lifts}
                    onChange={(e) => handleWeightChange('lifts', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* 雪道长度指标 */}
            <div className="weight-group">
              <h3>不同雪道长度</h3>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">初级道总长</span>
                    <span className="weight-value">{weights.beginnerTrails}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.beginnerTrails}
                    onChange={(e) => handleWeightChange('beginnerTrails', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">中级道总长</span>
                    <span className="weight-value">{weights.intermediateTrails}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.intermediateTrails}
                    onChange={(e) => handleWeightChange('intermediateTrails', Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">高级道总长</span>
                    <span className="weight-value">{weights.advancedTrails}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.advancedTrails}
                    onChange={(e) => handleWeightChange('advancedTrails', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item"></div>
              </div>
            </div>

            {/* 气候指标 */}
            <div className="weight-group">
              <h3>气候指标</h3>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">年均降雪</span>
                    <span className="weight-value">{weights.snowFall}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.snowFall}
                    onChange={(e) => handleWeightChange('snowFall', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">雪均深</span>
                    <span className="weight-value">{weights.snowDepth}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.snowDepth}
                    onChange={(e) => handleWeightChange('snowDepth', Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">积雪期</span>
                    <span className="weight-value">{weights.snowExistDays}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.snowExistDays}
                    onChange={(e) => handleWeightChange('snowExistDays', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">降雪日</span>
                    <span className="weight-value">{weights.snowDaysYearly}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.snowDaysYearly}
                    onChange={(e) => handleWeightChange('snowDaysYearly', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* 环境指标 */}
            <div className="weight-group">
              <h3>环境指标</h3>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">温度评分</span>
                    <span className="weight-value">{weights.temperature}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.temperature}
                    onChange={(e) => handleWeightChange('temperature', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">风况评分</span>
                    <span className="weight-value">{weights.windSpeed}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.windSpeed}
                    onChange={(e) => handleWeightChange('windSpeed', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* 交通指标 */}
            <div className="weight-group">
              <h3>交通指标</h3>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">距城市</span>
                    <span className="weight-value">{weights.cityDistance}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.cityDistance}
                    onChange={(e) => handleWeightChange('cityDistance', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">距大城市</span>
                    <span className="weight-value">{weights.majorCityDistance}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.majorCityDistance}
                    onChange={(e) => handleWeightChange('majorCityDistance', Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">距机场</span>
                    <span className="weight-value">{weights.airportDistance}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.airportDistance}
                    onChange={(e) => handleWeightChange('airportDistance', Number(e.target.value))}
                  />
                </div>
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">距火车站</span>
                    <span className="weight-value">{weights.trainDistance}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.trainDistance}
                    onChange={(e) => handleWeightChange('trainDistance', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* 价格指标 */}
            <div className="weight-group">
              <h3>价格指标</h3>
              <div className="weight-row">
                <div className="weight-item">
                  <div className="weight-header">
                    <span className="weight-label">价格评分</span>
                    <span className="weight-value">{weights.price}分</span>
                  </div>
                  <input 
                    type="range"
                    className="weight-slider"
                    min="0" 
                    max="50" 
                    step="5"
                    value={weights.price}
                    onChange={(e) => handleWeightChange('price', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* 底部按钮 */}
            <div className="weight-footer">
              <button className="weight-btn weight-reset-btn" onClick={() => setWeights(DEFAULT_WEIGHTS)}>
                重置权重
              </button>
              <button className="weight-btn weight-snow-btn" onClick={() => setWeights(SNOW_WEIGHTS)}>
                雪况权重
              </button>
          </div>
        </div>
      )}

      {/* 加载状态 */}
      {loading && (
        <div className="loading">
          加载中...
        </div>
      )}

      {/* 排行榜列表 */}
      {!loading && !error && (
        <div className="ranking-list">
          {displayResorts.map((resort, index) => (
              <div 
                key={resort.id} 
                className={`list-item ${selectedResort?.id === resort.id ? 'selected' : ''}`}
                onClick={() => handleResortClick(resort)}
              >
              <span className="col-rank">
                {index < 3 ? (
                  <img 
                      src={index === 0 ? ranking1Icon : index === 1 ? ranking2Icon : ranking3Icon}
                    alt={`Rank ${index + 1}`}
                    className="rank-medal"
                  />
                ) : (
                  index + 1
                )}
              </span>
              <div className="col-resort">
                  <div className="resort-title">
                    <span className="resort-name">{resort.name}</span>
                    <span className="resort-location">
                      {resort.country}{resort.region ? ` · ${resort.region}` : ''}
                    </span>
                </div>
                <span className="resort-details">{resort.details}</span>
              </div>
              <span className="col-score">{resort.score}</span>
            </div>
          ))}
          
          {hasMore && (
            <div className="load-more" onClick={handleLoadMore}>
              加载更多
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default RankingPanel; 